/**
 * Strip sensitive data from an error object before it goes to a
 * logger / Sentry / PostHog.
 *
 * Axios attaches the resolved request `config` to its `AxiosError`,
 * which includes the live `Authorization: Bearer <jwt>`, cookies, and
 * the request body (a login post carries `{ email, password }`). The
 * `response` likewise carries headers (`set-cookie`) and the response
 * body (a token endpoint returns the `access_token`).
 *
 * Our api client wraps non-OK responses into `ApiError` and never
 * copies these along — but it DOES attach the original `AxiosError` as
 * `ApiError.cause` (for network/timeout errors especially). So a secret
 * can hide one level down the `cause` chain even when the top-level
 * error looks clean. We therefore walk the chain (bounded depth) and
 * redact at every level.
 *
 * Structure-preserving: we clone and blank only the known-sensitive
 * fields (sensitive headers, request/response bodies, basic-auth), and
 * keep everything else — message, stack, status, code, url, method — so
 * the log stays useful for debugging.
 */

const REDACTED_HEADERS = ['authorization', 'cookie', 'x-csrf-token', 'set-cookie'] as const
const REDACTED = '[redacted]'
/** Guard against pathological / cyclic cause chains. */
const MAX_CAUSE_DEPTH = 5

type HeaderBag = Record<string, unknown>
type Bag = Record<string, unknown>

function redactHeaderBag(headers: HeaderBag): HeaderBag {
  const out: HeaderBag = {}
  for (const [k, v] of Object.entries(headers)) {
    out[k] = REDACTED_HEADERS.includes(k.toLowerCase() as (typeof REDACTED_HEADERS)[number])
      ? REDACTED
      : v
  }
  return out
}

/**
 * Redact an axios request `config`: sensitive headers, the request body
 * (`data`), and any HTTP basic-auth credentials. Bodies are blanket-
 * redacted — we can't allow-list per shape, and bodies routinely carry
 * passwords / tokens.
 */
function redactConfig(config: Bag): Bag {
  const out: Bag = { ...config }
  if (config.headers && typeof config.headers === 'object') {
    out.headers = redactHeaderBag(config.headers as HeaderBag)
  }
  if ('data' in config && config.data !== undefined) out.data = REDACTED
  if ('auth' in config && config.auth !== undefined) out.auth = REDACTED
  return out
}

/** Redact an axios `response`: headers, the response body, nested config. */
function redactResponse(response: Bag): Bag {
  const out: Bag = { ...response }
  if (response.headers && typeof response.headers === 'object') {
    out.headers = redactHeaderBag(response.headers as HeaderBag)
  }
  if ('data' in response && response.data !== undefined) out.data = REDACTED
  if (response.config && typeof response.config === 'object') {
    out.config = redactConfig(response.config as Bag)
  }
  return out
}

/**
 * Return a clone of `err` safe to log. Walks the `cause` chain so a
 * secret nested under `ApiError.cause` (the original AxiosError) is
 * redacted too. Non-object inputs, and plain errors with no axios
 * shape and no cause, are returned unchanged so we don't reshape a
 * normal stack trace into a bare object.
 */
export function redactErrorForLog(err: unknown, _depth = 0): unknown {
  if (!err || typeof err !== 'object' || _depth > MAX_CAUSE_DEPTH) return err

  const c = err as Bag
  const hasConfig = !!(c.config && typeof c.config === 'object')
  const hasResponse = !!(c.response && typeof c.response === 'object')
  // `cause` is a non-enumerable own property on real Errors — read it
  // directly rather than relying on a spread.
  const cause = (c as { cause?: unknown }).cause
  const hasCause = cause != null && typeof cause === 'object'

  if (!hasConfig && !hasResponse && !hasCause) return err

  // Clone own-enumerable props (keeps ApiError's kind/status/code/url/
  // method etc.). Error's name/message/stack are non-enumerable, so
  // copy them explicitly.
  const clone: Bag = { ...c }
  const e = c as { name?: unknown; message?: unknown; stack?: unknown }
  if (typeof e.name === 'string') clone.name = e.name
  if (typeof e.message === 'string') clone.message = e.message
  if (typeof e.stack === 'string') clone.stack = e.stack

  if (hasConfig) clone.config = redactConfig(c.config as Bag)
  if (hasResponse) clone.response = redactResponse(c.response as Bag)
  if (hasCause) clone.cause = redactErrorForLog(cause, _depth + 1)

  return clone
}
